import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { compact } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { JOTFORM_FORMS_QUERY } from '../graphql'
import { REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { JotformForm } from 'app/generated/graphql'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  selection: z
    .array(
      z.object({
        id: z.string(),
        meta: z.object({
          type: z.string(),
          labelId: z.string().optional(),
          labelName: z.string().optional(),
        }),
      }),
    )
    .default([])
    .transform((val) => val.map((v) => ({ id: v.id, type: v.meta.type, labelId: v.meta.labelId }))),
})

export const createJotformValidator = withZod(createSchema)

// The endpoint's rejection is one sentence (67 chars for the in-use message). Anything
// appreciably longer is a stack trace or an error page, not something to put in a toast.
const MAX_TOAST_REASON_LENGTH = 200

type JotformFormsQueryResult = {
  integrationOptions: {
    jotformForms: JotformForm[]
  }
}

export function JotformFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    jotform: string[] | LocationType[`jotformV2`]
  }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [selectedOptions, setSelectedOptions] = useState<OptionType[] | undefined>(undefined)
  const [refreshing, setRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirm] = useConfirmation()

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<JotformFormsQueryResult>({
    query: JOTFORM_FORMS_QUERY,
    variables: { locationId: currentValues?.locationId },
    requestPolicy: `network-only`,
  })

  const selectedIds = useMemo(() => new Set(selectedOptions?.map((option) => option.id)), [selectedOptions])

  const options = useMemo<OptionType[]>(() => {
    if (!data?.integrationOptions?.jotformForms) return []
    // A selected form stays available even when the API flags it, so it can still be unchecked.
    const isConnected = (form: JotformForm) => form.isUsed && !selectedIds.has(form.id)
    const byName = (a: JotformForm, b: JotformForm) =>
      a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
    const forms = data.integrationOptions.jotformForms

    return [...forms.filter((form) => !isConnected(form)).sort(byName), ...forms.filter(isConnected).sort(byName)].map(
      (form) => ({
        id: form.id,
        name: `${form.name}${form.labelName ? ` (${form.labelName})` : ``}`,
        value: form.id,
        meta: {
          group: isConnected(form) ? `Connected` : `Available`,
          isUsed: isConnected(form),
          subtext: isConnected(form) ? form.usedByLocationName : undefined,
          tooltipForDisabledOption: form.usedByLocationName
            ? `This form is connected to ${form.usedByLocationName}`
            : `This form is connected to another location`,
          type: `form`,
          labelId: form.labelId,
          labelName: form.labelName,
        },
      }),
    )
  }, [data, selectedIds])

  // Handle errors and clear refreshing state
  useEffect(() => {
    if (error) {
      logError(`Failed to load Jotform options:`, error)
      notifyError(`There was a problem loading options. You may need to reload the page.`)
    }
    if (!fetching) setRefreshing(false)
  }, [error, fetching])

  // Set selected options when data loads and we have current values
  useEffect(() => {
    if (data?.integrationOptions?.jotformForms && currentValues?.jotform?.length) {
      // Handle form IDs - can be either direct string array or jotformV2 structure
      let formIds: string[] = []

      // Check if it's a string array (jotformFormIds) or object array (jotformV2)
      if (typeof currentValues.jotform[0] === `string`) {
        formIds = currentValues.jotform as unknown as string[]
      } else {
        // Legacy jotformV2 structure with labelId - treat labelId as formId for backward compat
        formIds = (currentValues.jotform as { labelId?: string }[])
          .map((config) => config.labelId)
          .filter(Boolean) as string[]
      }

      const selectedForms: OptionType[] = compact(
        formIds.map((formId) => {
          const form = data.integrationOptions.jotformForms.find((f) => f.id === formId)
          if (!form) return undefined
          return {
            id: form.id,
            name: `${form.name}${form.labelName ? ` (${form.labelName})` : ``}`,
            value: form.id,
            meta: {
              type: `form`,
              labelId: form.labelId,
              labelName: form.labelName,
            },
          }
        }),
      )
      setSelectedOptions(selectedForms)
    }
  }, [data, currentValues?.jotform])

  const refresh = () => {
    setRefreshing(true)
    reexecuteQuery({ requestPolicy: `network-only` })
  }

  // The endpoint answers with JSON on success but plain text on rejection.
  const onSaveResponse = async (response: Response) => {
    const isJson = response.headers.get(`content-type`)?.includes(`application/json`)
    const result = isJson ? ((await response.json()) as { success?: boolean; error?: string }) : undefined

    if (response.ok && result?.success) {
      notifySuccess(`Jotform integration created`)
      onAdd()
      closeModal()
      return
    }

    const text = isJson ? result?.error : await response.text()
    const reason = text && text.length < MAX_TOAST_REASON_LENGTH && !text.trimStart().startsWith(`<`) ? text.trim() : ``
    notifyError(reason || `Failed to save integration`)
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove Jotform Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() => {
      setSaving(true)
      return gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `jotform`,
      })
        .then((result) => {
          if (result.data?.removeLocationIntegration?.success) {
            notifySuccess(`Jotform integration removed`)
            onAdd()
            closeModal()
          } else {
            notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
          }
        })
        .catch(() => notifyError(`Failed to remove integration`))
        .finally(() => setSaving(false))
    })
  }

  const onSubmitForm = () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    if (!locationId || !selectedOptions?.length) return

    const selection = selectedOptions.map((opt) => ({
      id: opt.id,
      type: opt.meta?.type || `form`,
      labelId: opt.meta?.labelId,
    }))

    const method = currentValues ? `PUT` : `POST`
    setSaving(true)
    window
      .fetch(`/api/integrations/jotform`, {
        method,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ locationId, selection }),
      })
      .then((response) => onSaveResponse(response))
      .catch(() => notifyError(`Failed to save integration`))
      .finally(() => setSaving(false))
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createJotformValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSubmitForm}
        action="/api/integrations/jotform"
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.jotformFormIds?.length,
                  tooltipForDisabledOption: `This location is already connected to Jotform`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load forms</div>
        ) : options.length === 0 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No forms available</div>
        ) : (
          <FormComboMultiSelect
            cols={5}
            name="selection"
            label="Forms"
            values={selectedOptions}
            options={options}
            labelClassName="text-body-sm text-strong"
            onChange={(opts) => setSelectedOptions(opts)}
            newLineError={false}
            checkboxSide="left"
            isOptionsGrouped
            showErrorIcon
          />
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
        {selectedOptions && selectedOptions.length > 0 && (
          <div className="col-span-6 flex flex-wrap gap-2">
            {selectedOptions.map((option) => (
              <div
                key={option.id}
                className="inline-flex items-center gap-2 rounded-full bg-surface-muted px-3 py-1 text-sm text-default"
              >
                <span>{option.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedOptions(selectedOptions.filter((o) => o.id !== option.id))
                  }}
                  className="text-subtle hover:text-default"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? (
              <Button
                label={saving ? `Removing...` : `Remove`}
                mode="danger"
                size="md"
                disabled={saving}
                onClick={() => onRemove()}
              />
            ) : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" disabled={saving} onClick={() => closeModal()} />
            )}
            <Button
              type="submit"
              form="integration-form"
              label={saving ? `Saving...` : `Save`}
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !selectedOptions || saving}
            />
          </div>
        </div>
      </div>
    </>
  )
}
